import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ConsentPurpose } from "@talenthop/shared";
import { api } from "../../lib/api.js";
import { Check, Info } from "../../components/icons.js";

/**
 * Candidate · withdraw a consent (GET /invite/{token}/consents, POST …/consent/withdraw).
 *
 * The consent screen promises "you can withdraw any consent later"; this is later. It lives on
 * the completion screen beside the export/erase requests because that is the one page a
 * candidate can always get back to with the link they already have.
 *
 * The tone is deliberately flat. Withdrawing is a right, not an escalation or a complaint, so
 * there is no warning banner, no red button and no "are you sure you want to lose…" framing —
 * just what stops, what stays, and where erasure lives. Every purpose says both halves, because
 * "we stopped processing" and "the data is gone" are different things and conflating them is
 * how a candidate ends up believing their recording was deleted when it was not.
 */

interface ConsentStateDto {
  purpose: ConsentPurpose;
  state: "granted" | "withdrawn";
  active: boolean;
  grantedAt: string;
  withdrawnAt: string | null;
}
interface ConsentsResponse {
  consents: ConsentStateDto[];
}

/** What each purpose means in plain words, and what withdrawing it actually changes. */
const withdrawCopy = (
  retentionDays: number,
): Record<ConsentPurpose, { title: string; stops: string; stays: string }> => ({
  interview_capture: {
    title: "Recording my interview",
    stops: "We stop recording you. Nothing further is captured, and no new interview can be recorded on this link.",
    stays: `The recording already made stays until it is automatically deleted, ${retentionDays} days after your interview. Your written answers and your assessment stay with the hiring team.`,
  },
  biometric_liveness: {
    title: "Face match & liveness check",
    stops: "No further identity or liveness check runs.",
    stays: "The verified / not-verified result already on your record stays. We never held your face scan itself.",
  },
  aadhaar_verify: {
    title: "Government ID match",
    stops: "No further ID match runs.",
    stays: "The verified / not-verified result stays. Your ID number was never stored.",
  },
  model_training: {
    title: "Anonymised model improvement",
    stops: "Your answers are excluded from any work to calibrate the assessment engine.",
    stays: "Your interview and assessment stay with the hiring team — this consent was never about them.",
  },
  persona_reuse_per_org: {
    title: "Reusing my profile for other roles",
    stops: "Your verified profile stops being offered to other roles or companies.",
    stays: "The application you already made keeps its access. This does not withdraw you from it.",
  },
  cross_border_access: {
    title: "Cross-border access",
    stops: "Overseas teams lose access to your verified report.",
    stays: "Your data stays stored in your own region, as it always was.",
  },
  raw_media_transfer: {
    title: "Sending the recording to the employer",
    stops: "No further transfer of the recording itself.",
    stays: "Anything already sent stays with them; ask them directly, or use Erase below to reach us.",
  },
});

export function ConsentWithdraw({
  token,
  retentionDays,
}: {
  token: string;
  retentionDays: number;
}): ReactNode {
  const queryClient = useQueryClient();
  const [opened, setOpened] = useState<ConsentPurpose | null>(null);
  const copy = withdrawCopy(retentionDays);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["invite-consents", token],
    queryFn: () => api.get<ConsentsResponse>(`/invite/${token}/consents`),
    enabled: token.length > 0,
  });

  const withdraw = useMutation({
    mutationFn: (purpose: ConsentPurpose) =>
      api.post<ConsentsResponse>(`/invite/${token}/consent/withdraw`, { purposes: [purpose] }),
    // The response IS the new state, read back through the same helper the capture surfaces
    // use — so the row flips to "withdrawn" only once that is actually what will be enforced.
    onSuccess: (res) => {
      queryClient.setQueryData(["invite-consents", token], res);
      setOpened(null);
    },
  });

  // Nothing consented (or the ledger is empty) → no section at all, rather than an empty card.
  const consents = data?.consents ?? [];
  if (isLoading || (consents.length === 0 && !isError)) return null;

  if (isError) {
    return (
      <div style={{ padding: "10px 0", borderTop: "1px solid var(--line-soft)" }}>
        <div style={{ fontWeight: 600, fontSize: "13.4px" }}>Your consent choices</div>
        <div className="mut sm">
          Couldn't load them just now.{" "}
          <button className="btn sm ghost" style={{ marginLeft: 4 }} onClick={() => void refetch()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ borderTop: "1px solid var(--line-soft)", marginTop: 6, paddingTop: 10 }}>
      <div style={{ fontWeight: 600, fontSize: "13.4px" }}>Your consent choices</div>
      <div className="mut sm" style={{ marginBottom: 4 }}>
        You can withdraw any of these at any time. Withdrawing stops the processing from here on —
        it doesn't delete what has already been collected, which is a separate erasure request.
      </div>

      {consents.map((c) => {
        const text = copy[c.purpose] ?? {
          title: c.purpose.replace(/_/g, " "),
          stops: "We stop this processing.",
          stays: "Data already collected stays until erased.",
        };
        return (
          <div key={c.purpose} style={{ padding: "10px 0", borderBottom: "1px solid var(--line-soft)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: "13.4px" }}>{text.title}</div>
                <div className="mut sm">
                  {c.active
                    ? text.stops
                    : `Withdrawn${c.withdrawnAt ? ` on ${new Date(c.withdrawnAt).toLocaleDateString()}` : ""} — ${text.stays}`}
                </div>
              </div>
              {c.active ? (
                <button className="btn sm" onClick={() => setOpened(opened === c.purpose ? null : c.purpose)}>
                  Withdraw
                </button>
              ) : (
                <span className="pill n">Withdrawn</span>
              )}
            </div>

            {opened === c.purpose && c.active && (
              <div className="card pad" style={{ marginTop: 10, background: "var(--surface-2)" }}>
                <div style={{ fontWeight: 600, fontSize: "13.4px" }}>What happens</div>
                <div className="mut sm" style={{ marginTop: 2 }}>
                  <b>Stops:</b> {text.stops}
                </div>
                <div className="mut sm" style={{ marginTop: 2 }}>
                  <b>Stays:</b> {text.stays}
                </div>
                <div className="mut sm" style={{ marginTop: 2 }}>
                  To have what's already held deleted as well, use <b>Erase your data</b> above — that's a
                  separate request, and a person handles it.
                </div>
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 10 }}>
                  <button className="btn ghost sm" onClick={() => setOpened(null)}>
                    Keep it
                  </button>
                  <button
                    className="btn primary sm"
                    disabled={withdraw.isPending}
                    onClick={() => withdraw.mutate(c.purpose)}
                  >
                    <Check /> {withdraw.isPending ? "Withdrawing…" : "Withdraw consent"}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {withdraw.isError && (
        <div className="banner crit" style={{ marginTop: 10 }}>
          <Info />
          <span>Couldn't record that withdrawal. Please retry in a moment — nothing has changed yet.</span>
        </div>
      )}
    </div>
  );
}
